import { useApolloClient, useQuery } from '@apollo/client';
import classNames from 'classnames';
import { inflect, pluralize } from 'inflection';
import * as _ from 'lodash-es';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import { VariableSizeList } from 'react-window';
import {
  DragTypes,
  LOCAL_TOPIC_ID_PREFIX,
  MoveStates,
  TopicStatus,
  UNORGANIZED_TOPIC_ID,
} from './constants';
import * as styles from './ContentMap.module.scss';
import ContentMapColumn, { SPECIAL_COLUMN_PADDING } from './ContentMapColumn';
import ContentMapDragLayer from './ContentMapDragLayer';
import ContentMapDroppableSeparator from './ContentMapDroppableSeparator';
import ContentMapFilter from './ContentMapFilter';
import ContentMapNewTopicButton from './ContentMapNewTopicButton';
import TopicBreadcrumbs from './TopicBreadcrumbs';
import topicPolicyCheck, { TopicPolicyAction } from './topicPolicyCheck';
import useAllTopics, { useRefetchAllTopics } from './useAllTopics';
import useTopicReorganization, {
  createSpecialTopic,
} from './useTopicReorganization';
import type {
  PostEdge,
  TopicForReorganization,
  TopicPosts,
} from './useTopicReorganization';
import WindowItem from './WindowItem';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import type { ButtonProps } from '../components/Button';
import ConfirmationModal from '../components/ConfirmationModal';
import EmptyState from '../components/EmptyState';
import LoadingIndicator from '../components/LoadingIndicator';
import { MenuItem } from '../components/Menu';
import OverflowMenu from '../components/OverflowMenu';
import ALL_UNORGANIZED_POSTS_QUERY from '../graphql/allUnorganizedPosts';
import NewTopicModal from './modal/NewTopicModal';
import type { ContentMapGetDropParams, DragObject } from './utils/types';
import { gql } from '../graphql/generated';
import useBatchDataLoader from './utils/useBatchDataLoader';
import { UserType } from '../graphql/generated/graphql';
import type { ContentMapColumn_PostsMapFragment } from '../graphql/generated/graphql';
import collapseIcon from '../icons/non-prefilled/16x16/collapse.svg';
import expandIcon from '../icons/non-prefilled/16x16/expand.svg';
import postIcon from '../icons/non-prefilled/16x16/post.svg';
import { useAllSlimPostsWithoutTopics } from '../post/useAllSlimPosts';
import { UIActions } from '../ui/uiSlice';
import { useBeforeLeaveConfirm } from '../utils/BeforeLeaveConfirm';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import memoizeDndCollect from '../utils/memoizeDndCollect';
import Link from '../utils/router/Link';
import topicPath from '../utils/url/topicPath';
import { usePublicContentPermissions } from '../utils/useAllowPublicContentBy';

const MAX_UNORGANIZED_POSTS = 1000;

const COLUMN_WIDTH = parseInt(styles.columnWidth, 10);
const SEPARATOR_WIDTH = parseInt(styles.separatorWidth, 10);

const useUnorganizedPostCount = ({ skip = false }) => {
  const { data } = useQuery(ALL_UNORGANIZED_POSTS_QUERY, {
    skip,
    variables: { first: 0 },
  });

  return data?.unorganized?.totalCount;
};

const useAllUnorganizedPosts = ({ skip = false }) => {
  const response = useQuery(ALL_UNORGANIZED_POSTS_QUERY, {
    skip,
    variables: { first: 200 },
  });

  const fetchMore = ConnectionHelper.generateFetchMore(
    ALL_UNORGANIZED_POSTS_QUERY,
    response,
    'unorganized',
  );

  const { data, loading } = response;

  const posts = useMemo(
    () => _.compact(data?.unorganized?.edges?.map((edge) => edge?.node)),
    [data],
  );

  useEffect(() => {
    if (fetchMore && posts.length < MAX_UNORGANIZED_POSTS && !loading) {
      fetchMore();
    }
  }, [fetchMore, loading, posts.length]);

  return posts;
};

const getTopicIds = (item: DragObject) => item.toMove.topicIds.toReversed();

interface States {
  allExpanded: boolean;
  isShowingPosts: boolean;
  visibleColumnTopicIds: string[];
}

const SESSION_STORAGE_KEY = 'ContentMapStates';
const saveStates = (states: States) => {
  sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(states));
};

const useInitialStates = (defaults: States): States => {
  const initialStatesRef = useRef<States | null>(null);

  if (!initialStatesRef.current) {
    initialStatesRef.current = defaults;
    const json = sessionStorage.getItem(SESSION_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    if (json) {
      try {
        const newStates = { ...defaults, ...JSON.parse(json) };
        initialStatesRef.current = newStates;
        return newStates;
      } catch (e) {
        return { ...defaults };
      }
    }
  }

  return initialStatesRef.current;
};

const POSTS_QUERY = gql(/** @complexity 2 */ `
  query ContentMapPosts($ids: [ID!]!) {
    posts(ids: $ids) {
      id
      ...ContentMapColumn_postsMap
    }
  }
`);

/**
 * A map of post IDs to their corresponding data.
 * `useAllSlimPosts` is used before the map has the data.
 */
const usePostsMapManager = () => {
  const client = useApolloClient();
  const [postsMap, setPostsMap] = useState<
    Record<string, ContentMapColumn_PostsMapFragment>
  >({});

  const loadPost = useBatchDataLoader(async (ids) => {
    const { data } = await client.query({
      query: POSTS_QUERY,
      variables: { ids },
      fetchPolicy: 'network-only',
    });
    const map: typeof postsMap = {};
    data.posts?.forEach((p) => {
      if (p.__typename !== 'Post') return;
      map[p.id] = p;
    });
    setPostsMap((current) => ({ ...current, ...map }));
  });

  return [postsMap, loadPost] as const;
};

const TOPIC_POSTS_QUERY = gql(/** @complexity 2 */ `
  query ContentMapTopicPosts($ids: [ID!]!) {
    topics(ids: $ids) {
      id
      ... on Topic {
        pinnedPostIds
      }
    }
  }
`);

/**
 * A map of topic IDs to their corresponding post edges.
 */
const useTopicPostsMapManager = () => {
  const client = useApolloClient();
  const allSlimPosts = useAllSlimPostsWithoutTopics();

  const [postEdgesMap, setPostEdgesMap] = useState<Record<string, PostEdge[]>>(
    {},
  );

  const cachedTopicPostsMap = useMemo(() => {
    const map: Record<string, Extract<TopicPosts, { status: 'cached' }>> = {};
    allSlimPosts.forEach((post) => {
      post.topics.forEach((topic) => {
        const oldPostIds = map[topic.id]?.postIds ?? [];
        const postIds =
          post.publishedAt && !post.archivedAt
            ? [...oldPostIds, post.id]
            : oldPostIds;

        map[topic.id] = {
          status: 'cached',
          postIds,
        };
      });
    });

    return map;
  }, [allSlimPosts]);

  const loadPostEdges = useBatchDataLoader(async (ids) => {
    const { data } = await client.query({
      query: TOPIC_POSTS_QUERY,
      variables: { ids },
      fetchPolicy: 'network-only',
    });
    const map: typeof postEdgesMap = {};
    data.topics?.forEach((t) => {
      if (t.__typename !== 'Topic') return;

      const allPosts = cachedTopicPostsMap[t.id]?.postIds ?? [];
      const edges: PostEdge[] = allPosts
        .map((postId) => {
          const pinnedPostIndex = t.pinnedPostIds.indexOf(postId);
          return {
            postId,
            pinnedOrder: pinnedPostIndex === -1 ? null : pinnedPostIndex,
          };
        })
        .toSorted(
          (a, b) =>
            (a.pinnedOrder ?? allPosts.length) -
            (b.pinnedOrder ?? allPosts.length),
        );
      map[t.id] = edges;
    });
    setPostEdgesMap((current) => ({ ...current, ...map }));
  });

  const topicPostsMap = useMemo(() => {
    const map: Record<string, TopicPosts> = { ...cachedTopicPostsMap };
    Object.entries(postEdgesMap).forEach(([id, edges]) => {
      map[id] = { status: 'loaded', edges };
    });
    return map;
  }, [cachedTopicPostsMap, postEdgesMap]);

  return [topicPostsMap, loadPostEdges] as const;
};

interface ContentMapProps {
  topicId?: string;
}

const ContentMap = ({ topicId }: ContentMapProps) => {
  const [postsMap, loadPost] = usePostsMapManager();
  const [topicPostsMap, loadPostEdges] = useTopicPostsMapManager();

  const isShowingAllTopics = !topicId;

  const dispatch = useAppDispatch();
  const currentUser = useAppSelector((state) => state.session.user);

  const initialStates = useInitialStates({
    allExpanded: true,
    isShowingPosts: true,
    visibleColumnTopicIds: [],
  });
  const [isAllExpanded, setIsAllExpanded] = useState(initialStates.allExpanded);
  const [isShowingPosts, setIsShowingPosts] = useState(
    initialStates.isShowingPosts,
  );
  const [visibleColumnTopicIds, setVisibleColumnTopicIds] = useState(
    new Set(initialStates.visibleColumnTopicIds),
  );
  const [isSaving, setIsSaving] = useState(false);
  const [width, setWidth] = useState<number | null>(null);
  const listRef = useRef<VariableSizeList | null>(null);
  const boardRef = useRef<HTMLDivElement | null>(null);

  const [isSaved, setIsSaved] = useState(false);

  const allTopics = useAllTopics();
  const flattenedAllTopics = useAllTopics({ flatten: true });
  const refetchAllTopics = useRefetchAllTopics();

  const unorganizedPostCount = useUnorganizedPostCount({
    skip: !isShowingAllTopics,
  });
  const { isPermittedToCreatePublicSubtopic } = usePublicContentPermissions();

  const topic = useMemo(
    () => flattenedAllTopics.find(({ id }) => id === topicId),
    [flattenedAllTopics, topicId],
  );
  const normalModeTopics = topic ? topic.childTopics : allTopics;
  const [reorganizeModeTopics, setReorganizeModeTopics] = useState<
    TopicForReorganization[] | null
  >(null);

  const topics = reorganizeModeTopics || normalModeTopics;
  const isReorganizing = !!reorganizeModeTopics;

  const isShowingUnorganizedColumn = isReorganizing && isShowingAllTopics;
  const unorganizedPosts = useAllUnorganizedPosts({
    skip: !isShowingUnorganizedColumn,
  });

  const {
    tree,
    selectedPosts,
    changeCount,
    reset,
    save,
    setPostSelected,
    setPostDeleted,
    setPostPinned,
    setPostArchived,
    setTopicSelected,
    setTopicName,
    setTopicDeleted,
    createSubtopic,
    moveTopic,
    movePost,
    movePinnedPost,
  } = useTopicReorganization({
    topic,
    topics: useMemo(
      () =>
        isShowingUnorganizedColumn
          ? [
              ...topics,
              createSpecialTopic({
                id: UNORGANIZED_TOPIC_ID,
                name: 'Unorganized posts',
              }),
            ]
          : topics,
      [isShowingUnorganizedColumn, topics],
    ),
    topicPostsMap: useMemo(
      () =>
        isShowingUnorganizedColumn
          ? {
              ...topicPostsMap,
              [UNORGANIZED_TOPIC_ID]: {
                status: 'loaded',
                edges: unorganizedPosts.map((post) => ({
                  postId: post.id,
                  pinnedOrder: null,
                })),
              },
            }
          : topicPostsMap,
      [isShowingUnorganizedColumn, topicPostsMap, unorganizedPosts],
    ),
  });

  useBeforeLeaveConfirm({ skip: !changeCount || isSaved });

  const handleSave = () => {
    dispatch(UIActions.openModal(() => <LoadingIndicator delay={0} card />));
    setIsSaving(true);
    return save().finally(() => {
      setIsSaved(true);
      saveStates({
        allExpanded: isAllExpanded,
        isShowingPosts,
        visibleColumnTopicIds: [...visibleColumnTopicIds].filter(
          (id) => !id.startsWith(LOCAL_TOPIC_ID_PREFIX),
        ),
      });
      refetchAllTopics().finally(() => {
        // need to give apollo cache enough time to write to storage
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      });
    });
  };

  const getDropParams = useCallback<ContentMapGetDropParams>(
    (
      accept,
      {
        destinationTree,
        indexWithPlaceholder,
        pinnedPostIndexWithPlaceholder,
        canDrop = () => true,
      },
    ) => ({
      accept,
      drop: (item) => {
        const topicIds = getTopicIds(item);
        const postKeys =
          item.type === DragTypes.column
            ? []
            : item.toMove.postKeys.toReversed();
        const pinnedPostKeys =
          item.type === DragTypes.column
            ? []
            : item.toMove.pinnedPostKeys.toReversed();
        const destinationId = destinationTree.topic.id;

        if (typeof indexWithPlaceholder === 'number') {
          topicIds.forEach((id) => {
            moveTopic({ id, destinationId, indexWithPlaceholder });
            setTopicSelected({ id, selected: false });
          });
        }

        postKeys.forEach((postKey) => {
          movePost({ postKey, destinationId });
          setPostSelected({ postKey, selected: false });
        });

        if (typeof pinnedPostIndexWithPlaceholder === 'number') {
          pinnedPostKeys.forEach((postKey) => {
            movePinnedPost({
              postKey,
              destinationId,
              indexWithPlaceholder: pinnedPostIndexWithPlaceholder,
            });
            setPostSelected({ postKey, selected: false });
          });
        }

        if (visibleColumnTopicIds.size > 0) {
          if (
            item.type !== DragTypes.column &&
            destinationTree.topic.id === topicId
          ) {
            setVisibleColumnTopicIds(
              new Set([...visibleColumnTopicIds, ...topicIds]),
            );
          } else if (
            item.type === DragTypes.column &&
            destinationTree.topic.id !== topicId
          ) {
            const newSelectedIds = new Set(visibleColumnTopicIds);
            topicIds.forEach((t) => newSelectedIds.delete(t));
            setVisibleColumnTopicIds(newSelectedIds);
          }
        }
      },
      canDrop: (item) =>
        accept.includes(item.type) &&
        destinationTree.reorganizationMetadata.move.state !==
          MoveStates.movedOut &&
        getTopicIds(item).every(
          (id) =>
            id !== destinationTree.topic.id &&
            destinationTree.topic.ancestors.every(
              (ancestor) => ancestor.id !== id,
            ),
        ) &&
        canDrop(item),
      collect: memoizeDndCollect((monitor) => ({
        isOver: monitor.canDrop() && monitor.isOver({ shallow: true }),
        isDragging: !!monitor.getItemType(),
      })),
    }),
    [
      movePinnedPost,
      movePost,
      moveTopic,
      setPostSelected,
      setTopicSelected,
      topicId,
      visibleColumnTopicIds,
    ],
  );

  const handleUpdateReorganizing = useCallback(
    (newIsReorganizing: boolean) => {
      setReorganizeModeTopics(newIsReorganizing ? normalModeTopics : null);
    },
    [normalModeTopics],
  );

  const handleToggleExpand = () => setIsAllExpanded(!isAllExpanded);

  const handleCreateTopic = useCallback(
    (reorganizeMode: boolean) => {
      const parentId = topicId;
      if (reorganizeMode) {
        const topicTempId = _.uniqueId(LOCAL_TOPIC_ID_PREFIX);
        createSubtopic({ topicTempId, parentId: parentId ?? null });
        if (!parentId) {
          setVisibleColumnTopicIds((currentIds) =>
            currentIds.size > 0
              ? new Set([...currentIds, topicTempId])
              : currentIds,
          );
        }
        handleUpdateReorganizing(true);
      } else {
        dispatch(UIActions.openModal(NewTopicModal, { parentId }));
      }
    },
    [dispatch, topicId, createSubtopic, handleUpdateReorganizing],
  );
  const createNewNoun = isShowingAllTopics ? 'topic' : 'subtopic';

  const columnTrees = useMemo(
    () =>
      tree.children.filter(
        ({ reorganizationMetadata }) =>
          reorganizationMetadata.move.state !== MoveStates.movedOut,
      ),
    [tree],
  );

  const columns = useMemo(() => {
    const selectedColumnTrees =
      visibleColumnTopicIds.size > 0
        ? columnTrees.filter(({ topic: { id } }) =>
            visibleColumnTopicIds.has(id),
          )
        : columnTrees;

    const sharedColumnProps = {
      allExpanded: isAllExpanded,
      selectedPosts,
      getDropParams,
      showPosts: isShowingPosts,
      onPostSelectedChange: setPostSelected,
      onPostDeletedChange: setPostDeleted,
      onPostPinnedChange: setPostPinned,
      onPostArchivedChange: setPostArchived,
      onTopicSelectedChange: setTopicSelected,
      onTopicDeletedChange: setTopicDeleted,
      onTopicNameChange: setTopicName,
      onSubtopicCreate: createSubtopic,
    };
    const result = selectedColumnTrees.map((columnTree) => ({
      key: columnTree.topic.id,
      render: (style: CSSProperties) => (
        <div style={style} className={styles.column}>
          <ContentMapDroppableSeparator
            {...getDropParams([DragTypes.column, DragTypes.topics], {
              destinationTree: tree,
              indexWithPlaceholder: tree.children.indexOf(columnTree),
            })}
          />
          <ContentMapColumn
            isReorganizing={isReorganizing}
            tree={columnTree}
            specialColumn={
              columnTree.topic.id === UNORGANIZED_TOPIC_ID
                ? 'unorganized'
                : null
            }
            loadPostEdges={loadPostEdges}
            postsMap={postsMap}
            loadPost={loadPost}
            {...sharedColumnProps}
          />
        </div>
      ),
      width:
        COLUMN_WIDTH +
        SEPARATOR_WIDTH +
        (columnTree.topic.id === UNORGANIZED_TOPIC_ID
          ? SPECIAL_COLUMN_PADDING
          : 0),
    }));

    if (
      topic &&
      isShowingPosts &&
      ((tree.reorganizationTopicPosts.status === 'loaded' &&
        tree.reorganizationTopicPosts.edges.length > 0) ||
        (tree.reorganizationTopicPosts.status === 'cached' &&
          tree.reorganizationTopicPosts.postIds.length > 0))
    ) {
      result.unshift({
        key: 'posts',
        render: (style) => (
          <div style={style} className={styles.column}>
            <ContentMapDroppableSeparator accept={[]} />
            <ContentMapColumn
              isReorganizing={isReorganizing}
              specialColumn="posts"
              tree={{
                topic: { ...topic, name: 'Posts' },
                children: [],
                isParentSelected: false,
                isSelected: false,
                reorganizationTopicPosts: tree.reorganizationTopicPosts,
                reorganizationMetadata: {
                  move: { state: MoveStates.initial },
                  status: TopicStatus.initial,
                },
                selected: tree.selected,
              }}
              loadPostEdges={loadPostEdges}
              postsMap={postsMap}
              loadPost={loadPost}
              {...sharedColumnProps}
            />
          </div>
        ),
        width: COLUMN_WIDTH + SEPARATOR_WIDTH + SPECIAL_COLUMN_PADDING,
      });
    }

    const isLastUnorganized = _.last(result)?.key === UNORGANIZED_TOPIC_ID;

    result.push({
      key: 'empty',
      render: (style) => (
        <div style={style} className={styles.column}>
          <ContentMapDroppableSeparator
            {...getDropParams(
              isLastUnorganized ? [] : [DragTypes.column, DragTypes.topics],
              {
                destinationTree: tree,
                indexWithPlaceholder: tree.children.length,
              },
            )}
          />
          <div className={styles.createColumn}>
            <EmptyState
              imageName="connections"
              title={`Create a new ${createNewNoun}`}
              message={`Use additional ${pluralize(
                createNewNoun,
              )} to organize posts and other ${pluralize(createNewNoun)}.`}
              action={
                <ContentMapNewTopicButton
                  topic={topic}
                  isPermittedToCreatePublicSubtopic={
                    isPermittedToCreatePublicSubtopic
                  }
                  onCreateTopic={handleCreateTopic}
                />
              }
              vertical
              smallIcon
              smallText
            />
          </div>
        </div>
      ),
      width: COLUMN_WIDTH + SEPARATOR_WIDTH,
    });

    return result;
  }, [
    isAllExpanded,
    handleCreateTopic,
    createNewNoun,
    getDropParams,
    isReorganizing,
    isShowingPosts,
    visibleColumnTopicIds,
    setPostSelected,
    setPostDeleted,
    setPostPinned,
    setPostArchived,
    setTopicSelected,
    setTopicDeleted,
    setTopicName,
    createSubtopic,
    tree,
    topic,
    columnTrees,
    isPermittedToCreatePublicSubtopic,
    selectedPosts,
    loadPostEdges,
    postsMap,
    loadPost,
  ]);

  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.resetAfterIndex(0);
    }
  }, [columns]);

  useLayoutEffect(() => {
    if (boardRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (boardRef.current) {
          setWidth(boardRef.current.getBoundingClientRect().width);
        }
      });
      resizeObserver.observe(boardRef.current);
      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [columns]);

  useEffect(() => {
    if (topicId) {
      loadPostEdges(topicId);
    }
  }, [topicId, loadPostEdges]);

  if (topicId && !topic) return null;

  const showPostsButtonProps: ButtonProps = {
    variant: 'secondary',
    onClick: () => setIsShowingPosts(!isShowingPosts),
    icon: postIcon,
    size: 'small',
  };
  const expandButtonProps: ButtonProps = {
    icon: isAllExpanded ? collapseIcon : expandIcon,
    variant: 'secondary',
    onClick: handleToggleExpand,
    size: 'small',
  };

  const desktopToolbar = (
    <div className={classNames(styles.toolbar, styles.desktop)}>
      {isShowingAllTopics && unorganizedPostCount ? (
        <Button
          variant="link"
          to="/unorganized"
          className={styles.unorganized}
          target="_blank"
        >
          Unorganized ({unorganizedPostCount})
        </Button>
      ) : null}
      <Button {...showPostsButtonProps}>
        {isShowingPosts ? 'Hide posts' : 'Show posts'}
      </Button>
      <Button {...expandButtonProps}>
        {isAllExpanded ? 'Collapse' : 'Expand'}
      </Button>
      {(currentUser.type === UserType.Admin ||
        (topic &&
          topicPolicyCheck(
            TopicPolicyAction.ReorganizeTopic,
            topic,
            currentUser,
          ))) &&
        columns.length > 0 && (
          <Button
            variant="primary"
            onClick={() => handleUpdateReorganizing(!isReorganizing)}
            size="small"
            disabled={isReorganizing}
          >
            Reorganize
          </Button>
        )}
    </div>
  );

  const mobileToolbar = (
    <div className={classNames(styles.toolbar, styles.mobile)}>
      <Button {...showPostsButtonProps} />
      <Button {...expandButtonProps} />
      {isShowingAllTopics && unorganizedPostCount ? (
        <OverflowMenu>
          <MenuItem to="/unorganized" icon={postIcon} newTab>
            Unorganized posts ({unorganizedPostCount})
          </MenuItem>
        </OverflowMenu>
      ) : null}
    </div>
  );

  let header = null;
  if (isShowingAllTopics) {
    header = <h2>Content Map</h2>;
  } else if (topic) {
    header = (
      <div>
        <div>
          <TopicBreadcrumbs
            topics={topic.ancestors}
            link
            showRoot
            tab="subtopics"
          />
        </div>
        <Link to={topicPath(topic)}>
          <h2>{topic.name}</h2>
        </Link>
      </div>
    );
  }

  if (!header) return null;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {header}
          {columnTrees.length > 0 && (
            <div className={styles.filter}>
              <ContentMapFilter
                columnTrees={columnTrees}
                selectedIds={visibleColumnTopicIds}
                onSelectedIdsChange={setVisibleColumnTopicIds}
              />
            </div>
          )}
        </div>
        {desktopToolbar}
        {mobileToolbar}
      </div>
      {columns.length > 1 ? (
        <div className={styles.topics} ref={boardRef}>
          {isReorganizing && <ContentMapDragLayer />}
          {typeof width === 'number' && (
            <VariableSizeList
              ref={listRef}
              layout="horizontal"
              height="100%"
              width={width}
              estimatedItemSize={_.sumBy(columns, 'width') / columns.length}
              itemKey={(i) => columns[i]!.key}
              itemCount={columns.length}
              itemSize={(i) => columns[i]!.width}
              itemData={columns.map(({ render }) => render)}
              overscanCount={0}
            >
              {WindowItem}
            </VariableSizeList>
          )}
        </div>
      ) : (
        <EmptyState
          imageName="connections"
          title={`No ${pluralize(createNewNoun)} yet…`}
          message={
            isShowingAllTopics
              ? 'Your topic tree will appear after new topics are created.'
              : 'Your topic tree will appear after new subtopics are created under this topic.'
          }
          action={
            <ContentMapNewTopicButton
              size="large"
              topic={topic}
              isPermittedToCreatePublicSubtopic={
                isPermittedToCreatePublicSubtopic
              }
              onCreateTopic={handleCreateTopic}
            />
          }
        />
      )}
      {isReorganizing && (
        <div className={styles.footer}>
          <div className={styles.info}>
            {changeCount > 0 ? changeCount : 'No'} unsaved{' '}
            {inflect('change', changeCount)}
          </div>
          <Button
            variant="secondary"
            onClick={() => {
              reset();
              handleUpdateReorganizing(false);
            }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() =>
              dispatch(
                UIActions.openModal(ConfirmationModal, {
                  title: 'Save changes?',
                  message: `You have made ${changeCount} ${inflect(
                    'change',
                    changeCount,
                  )} to your team’s topic and post hierarchy.`,
                  confirmButtonText: 'Save',
                  onConfirm: handleSave,
                }),
              )
            }
            disabled={changeCount === 0 || isSaving}
          >
            {isSaving ? <LoadingIndicator adaptColor delay={0} /> : 'Save'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ContentMap;
